import { useEffect, useState, memo, useMemo } from "react";

import { Grid, TextField, Skeleton } from "@mui/material";
import { useIntl } from "react-intl";

import TitledCard from "./basic/TitledCard";
import { hexInputCheck } from "./basic/utils";
import useEditedStyles from "../../hooks/useEditedStyles";
import { Accountinfo } from "../../utils/types";
import { useDevice } from "../../utils/DeviceProvider";
import { ResetAdornment, useResetContextMenu } from "../../utils/ResetContextMenuProvider";
import { useUserInfo } from "../../utils/user-info";

const AccountInfo = memo(() => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [account, setAccount] = useState("");

  const {
    device: { noConfig, currentValue, config, template },
    setCurrentValue,
    setEmptyConfig,
  } = useDevice();

  const { formatMessage } = useIntl();
  const editedStyle = useEditedStyles();
  const { openContextMenu } = useResetContextMenu();
  const [user] = useUserInfo();
  const limitedAccessUser = user?.rights.limited_user;

  const savedValue = useMemo<Accountinfo | null>(() => config?.toolboxConfig?.accountinfo || null, [config]);

  useEffect(() => {
    if (noConfig || (currentValue && !currentValue.accountinfo)) {
      const config = {
        name: "",
        description: "",
        account: "",
      };
      setEmptyConfig(config, "accountinfo");
      setCurrentValue((old: any) => ({ ...old, accountinfo: config }));
    }
  }, [currentValue, noConfig, setCurrentValue, setEmptyConfig]);

  useEffect(() => {
    if (currentValue?.accountinfo) {
      const { name, description, account } = currentValue.accountinfo;
      setName(name);
      setDescription(description);
      setAccount(account);
    }
  }, [currentValue]);

  if (!noConfig && !currentValue) {
    return (
      <TitledCard title={formatMessage({ defaultMessage: "Configuration info" })} stretch={true}>
        <Grid container spacing={1} sx={{ padding: 1 }}>
          <Grid item xs={12}>
            <Skeleton variant="rectangular" height={46.56} />
          </Grid>
          <Grid item xs={12}>
            <Skeleton variant="rectangular" height={113} />
          </Grid>
          <Grid item xs={12}>
            <Skeleton variant="rectangular" height={46.56} />
          </Grid>
        </Grid>
      </TitledCard>
    );
  }

  return (
    <TitledCard title={formatMessage({ defaultMessage: "Device info" })}>
      <Grid container spacing={1} sx={{ padding: 1 }}>
        <Grid item xs={12}>
          <TextField
            label={formatMessage({ defaultMessage: "Name" })}
            variant="filled"
            value={name}
            className={savedValue?.name !== name ? editedStyle.input : ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            onBlur={() => {
              currentValue.accountinfo.name = name;
              setCurrentValue(currentValue);
            }}
            InputProps={
              savedValue && savedValue.name !== name ? { endAdornment: <ResetAdornment />, readOnly: limitedAccessUser } : { readOnly: limitedAccessUser }
            }
            onContextMenu={
              savedValue && savedValue.name !== name ? (e: React.MouseEvent<HTMLElement, MouseEvent>) => openContextMenu(e, "accountinfo.name") : undefined
            }
            sx={{ margin: 0 }}
            fullWidth
            size="small"
            autoComplete="off"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={formatMessage({ defaultMessage: "Description" })}
            variant="filled"
            value={description}
            className={savedValue?.description !== description ? editedStyle.input : ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
            onBlur={() => {
              currentValue.accountinfo.description = description;
              setCurrentValue(currentValue);
            }}
            InputProps={
              savedValue && savedValue.description !== description
                ? { endAdornment: <ResetAdornment />, readOnly: limitedAccessUser }
                : { readOnly: limitedAccessUser }
            }
            onContextMenu={
              savedValue && savedValue.description !== description
                ? (e: React.MouseEvent<HTMLElement, MouseEvent>) => openContextMenu(e, "accountinfo.description")
                : undefined
            }
            multiline
            rows={4}
            sx={{ margin: 0 }}
            fullWidth
            size="small"
            autoComplete="off"
          />
        </Grid>
        {!template?.template?.info?.parsed ? (
          <Grid item xs={12}>
            <TextField
              label={formatMessage({ defaultMessage: "Account" })}
              variant="filled"
              value={account ?? ""}
              className={savedValue?.account !== account ? editedStyle.input : ""}
              inputProps={{ maxLength: 4, style: { textTransform: "uppercase" } }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => hexInputCheck(e.target.value, setAccount)}
              onBlur={() => {
                const v = account ? account.padStart(4, "0") : account;
                currentValue.accountinfo.account = v;
                setCurrentValue(currentValue);
              }}
              InputProps={
                savedValue && savedValue.account !== account
                  ? { endAdornment: <ResetAdornment />, readOnly: limitedAccessUser }
                  : { readOnly: limitedAccessUser }
              }
              onContextMenu={
                savedValue && savedValue.account !== account
                  ? (e: React.MouseEvent<HTMLElement, MouseEvent>) => openContextMenu(e, "accountinfo.account")
                  : undefined
              }
              size="small"
              fullWidth
              sx={{ margin: 0 }}
              autoComplete="off"
            />
          </Grid>
        ) : null}
      </Grid>
    </TitledCard>
  );
});

export default AccountInfo;
